import { Banner, BlockStack, Button, Text } from "@shopify/polaris";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { clearPendingBillingOperation, getBillingOperation, getCurrentBillingOperation,
  pendingBillingOperation, syncBilling, type BillingOperation } from "../api/billing";
import { getMe } from "../api/me";
import { ApiError } from "../api/client";
import { initLocale, t } from "../lib/i18n";
import { redirectTopLevel } from "../utils/redirect";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RETURN_PATH = /^(?:\/|\/archive|\/diagnosis|\/plans|\/invoices\/[1-9][0-9]{0,19})$/;
const STATUSES = new Set(["pending_approval", "active", "frozen", "cancelled", "declined", "expired"]);

/** URL と記憶領域には操作の識別子だけを置き、表示文脈は認証済み GET で読み直す。 */
export function BillingReturn({ children }: { children: ReactNode }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const id = params.get("billing_operation_id") ?? pendingBillingOperation();
  const returning = id !== null || params.has("billing_pending") || params.has("charge_id");
  return returning ? <Recovery key={id ?? "missing"} id={id}>{children}</Recovery> : children;
}

function validOperation(operation: BillingOperation): boolean {
  return UUID.test(operation.billing_operation_id) && RETURN_PATH.test(operation.return_path)
    && (operation.locale === "ja" || operation.locale === "en")
    && typeof operation.host === "string" && operation.host.length > 0
    && STATUSES.has(operation.subscription_status);
}

function savedLocation(operation: BillingOperation, pending: boolean) {
  const params = new URLSearchParams({ host: operation.host, locale: operation.locale });
  if (pending) {
    params.set("billing_pending", "1");
    params.set("billing_operation_id", operation.billing_operation_id);
  }
  return { pathname: operation.return_path, search: `?${params}` };
}

function Recovery({ id, children }: { id: string | null; children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const queryClient = useQueryClient();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{
    phase: "loading" | "context_error" | "error" | "pending" | "terminal" | "absent";
    operation?: BillingOperation;
  }>({ phase: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ phase: "loading" });
    async function reconcile() {
      let operation: BillingOperation | undefined;
      try {
        if (!id || (id !== "current" && !UUID.test(id))) throw new Error("billing_context_unavailable");
        const saved = id === "current" ? await getCurrentBillingOperation() : await getBillingOperation(id);
        if (cancelled) return;
        if (!validOperation(saved) || (id !== "current" && saved.billing_operation_id !== id)) {
          throw new Error("billing_context_unavailable");
        }
        operation = saved;
        initLocale(`?locale=${operation.locale}`);
        navigateRef.current(savedLocation(operation, true), { replace: true });
        // current の解決で識別子が変わるため、次の effect が具体的な操作を照合する。
        if (id === "current") return;
        if (operation.subscription_status === "pending_approval" || operation.subscription_status === "active"
          || operation.subscription_status === "frozen") {
          await syncBilling(id);
          if (cancelled) return;
          const refreshed = await getBillingOperation(id);
          if (cancelled) return;
          if (!validOperation(refreshed) || refreshed.billing_operation_id !== id
            || refreshed.host !== operation.host || refreshed.locale !== operation.locale
            || refreshed.return_path !== operation.return_path) throw new Error("billing_context_unavailable");
          operation = refreshed;
        }
        const me = await getMe();
        if (cancelled) return;
        if (operation.subscription_status === "active" && !me.subscription.active) {
          throw new Error("billing_entitlement_unconfirmed");
        }
        queryClient.setQueryData(["me"], me);
        const pending = operation.subscription_status === "pending_approval" || operation.subscription_status === "frozen";
        setState({ phase: pending ? "pending" : "terminal", operation });
      } catch (error) {
        if (!cancelled) setState({ phase: !operation && id === "current" && error instanceof ApiError
          && error.status === 404 && error.detail === "billing_operation_not_found"
          ? "absent" : operation ? "error" : "context_error", operation });
      }
    }
    void reconcile();
    return () => { cancelled = true; };
  }, [id, attempt, queryClient]);

  const { phase, operation } = state;
  const title = phase === "loading" ? "課金の状態を確認しています"
    : phase === "absent" ? "保存された課金操作はありません"
    : phase === "context_error" ? "課金の復帰情報を確認できませんでした"
    : phase === "error" ? "課金の状態を確認できませんでした"
    : phase === "pending" ? "課金の操作はまだ完了していません"
    : operation?.subscription_status === "active" ? "課金の状態を確認しました" : "課金の操作は終了しました";
  return <BlockStack gap="400">
    <Banner title={t(title)} tone={phase === "context_error" || phase === "error" ? "warning" : "info"}>
      <BlockStack gap="200">
        {phase === "pending" || phase === "error" ? <>
          <Text as="p">{t("保存された操作を再確認してください。新しい購読は開始しません。")}</Text>
          <Button onClick={() => setAttempt(value => value + 1)}>{t("状態を再確認")}</Button>
          {phase === "pending" && operation?.subscription_status === "pending_approval" && operation.confirmation_url
            ? <Button onClick={() => redirectTopLevel(operation.confirmation_url!)}>{t("Shopifyで承認を続ける")}</Button> : null}
        </> : null}
        {phase === "context_error" ? <>
          <Text as="p">{t("Shopify管理画面から開き直すか、この店舗で保存された課金操作を確認してください。")}</Text>
          <Button onClick={() => {
            if (id === "current") setAttempt(value => value + 1);
            else {
              const params = new URLSearchParams(location.search);
              params.set("billing_pending", "1");
              params.set("billing_operation_id", "current");
              navigate({ search: `?${params}` }, { replace: true });
            }
          }}>{t("保存された課金操作を確認")}</Button>
        </> : null}
        {phase === "terminal" && operation ? <Button onClick={() => {
          clearPendingBillingOperation(operation.billing_operation_id);
          navigate(savedLocation(operation, false), { replace: true });
        }}>{t("続ける")}</Button> : null}
        {phase === "absent" ? <Button onClick={() => {
          clearPendingBillingOperation();
          const params = new URLSearchParams(location.search);
          params.delete("billing_pending");
          params.delete("billing_operation_id");
          params.delete("charge_id");
          navigate({ pathname: "/plans", search: `?${params}` }, { replace: true });
        }}>{t("料金プランへ戻る")}</Button> : null}
      </BlockStack>
    </Banner>
    {phase !== "loading" ? children : null}
  </BlockStack>;
}
